import {useEffect, useRef, useState} from "react"
import axios from "axios"

/**
 * Hook qui gère le comportement de l'affichage de la modale d'acceptation du tradein, ainsi que de la modale de l'iframe du tradein
 * @param tunnelData
 * @param onNextStep
 * @param country
 * @param getOptinAnswers
 * @param finalFormValuesToTunnelData
 * @param pollingUrl - URL to poll when trade-in is accepted
 * @returns {{submit: submit, isTradeInEnable: boolean, showTradeInModal: boolean, showIframeModal: boolean, handleConfirm: handleConfirm, handleCancel: handleCancel, handleCloseIframe: handleCloseIframe}}
 */
export const useTradeInFlow = ({
                                 tunnelData,
                                 onNextStep,
                                 country,
                                 getOptinAnswers,
                                 finalFormValuesToTunnelData,
                                 pollingUrl
                               }) => {
  const [showTradeInModal, setShowTradeInModal] = useState(false)
  const [showIframeModal, setShowIframeModal] = useState(false)
  const [showLoaderModal, setShowLoaderModal] = useState(false)
  const [loaderStatus, setLoaderStatus] = useState("loading") // "loading", "found", "timeout"
  const [formValues, setFormValues] = useState(null)
  const isTradeInEnable = tunnelData?.data?.order?.tradeInEnabled
  const pollingIntervalRef = useRef(null)
  const pollingTimeoutRef = useRef(null)

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current)
      }
    }
  }, [])

  const submit = (values) => {
    setFormValues(values)

    if (values?.identity?.isSmallBusiness || !isTradeInEnable) {
      goNextPage(values)
    } else {
      setShowTradeInModal(true)
    }
  }

  const goNextPage = (values = formValues) => {
    const optins = getOptinAnswers(values)

    onNextStep({
      ...finalFormValuesToTunnelData(values, country, tunnelData),
      optins
    })
  }

  const handleConfirm = () => {
    setShowTradeInModal(false)
    setShowIframeModal(true)
  }

  const handleCancel = () => {
    setShowTradeInModal(false)
    goNextPage()
  }

  const handleCloseIframe = async (accepted) => {
    setShowIframeModal(false)
    console.log(`Le client a ${accepted ? "accepté" : "refusé"} le tradein`)

    pollingUrl = tunnelData.apiUrl + "api/v2/tradein/" + tunnelData.opl;

    if (accepted === true && pollingUrl) {
      // Show loader modal before starting polling
      setShowLoaderModal(true)
      setLoaderStatus("loading")

      const token = tunnelData.access_token;
      let timeoutLimit = 30
      let pollingInterval = 3

      const poll = async () => {
        try {
          const response = await axios.get(pollingUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "*/*"
            }
          })

          const {status, timeoutLimit: responseTimeoutLimit, pollingInterval: responsePollingInterval} = response.data

          if (status === "FOUND") {
            setLoaderStatus("found")
            clearInterval(pollingIntervalRef.current)
            clearTimeout(pollingTimeoutRef.current)
            pollingIntervalRef.current = null
            pollingTimeoutRef.current = null
            return
          }

          if (status === "PENDING") {
            // Update timeout and interval from response if provided
            if (responseTimeoutLimit !== undefined) {
              timeoutLimit = responseTimeoutLimit
            }
            if (responsePollingInterval !== undefined) {
              pollingInterval = responsePollingInterval
              // Restart interval with new value
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current)
              }
              pollingIntervalRef.current = setInterval(poll, pollingInterval * 1000)
            }
          }
        } catch (error) {
          setLoaderStatus("timeout")
          clearInterval(pollingIntervalRef.current)
          clearTimeout(pollingTimeoutRef.current)
          pollingIntervalRef.current = null
          pollingTimeoutRef.current = null
        }
      }

      // Initial request
      try {
        const initialResponse = await axios.get(pollingUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "*/*"
          }
        })

        const {
          status,
          timeoutLimit: responseTimeoutLimit,
          pollingInterval: responsePollingInterval
        } = initialResponse.data

        // Update timeout and interval from initial response if provided
        if (responseTimeoutLimit !== undefined) {
          timeoutLimit = responseTimeoutLimit
        }
        if (responsePollingInterval !== undefined) {
          pollingInterval = responsePollingInterval
        }

        if (status === "FOUND") {
          setLoaderStatus("found")
          return
        }

        if (status === "PENDING") {
          pollingIntervalRef.current = setInterval(poll, pollingInterval * 1000)

          // Set up timeout
          pollingTimeoutRef.current = setTimeout(() => {
            setLoaderStatus("timeout")
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
            pollingTimeoutRef.current = null
          }, timeoutLimit * 1000)
        }
      } catch (error) {
        setLoaderStatus("timeout")
      }
    }
  }

  const handleLoaderContinue = (isSuccess) => {
    setShowLoaderModal(false)
    if (isSuccess) {
      goNextPage()
    }
  }

  return {
    submit,
    isTradeInEnable,
    showTradeInModal,
    showIframeModal,
    showLoaderModal,
    loaderStatus,
    handleConfirm,
    handleCancel,
    handleCloseIframe,
    handleLoaderContinue
  }
}
